import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationCrosshairs, faHand, faMapMarkerAlt, faTimes, faSearch, faUser, faPencilAlt, faExpand, faCompress } from "@fortawesome/free-solid-svg-icons";
import apiAgent from "../../../../../../app/api/apiAgent";
import { toast } from "react-toastify";
import { createPropertyMarkers, createLegendItems } from "../utils";

// Add custom styles for the Google Places Autocomplete dropdown
const autocompleteStyles = `
  .pac-container {
    box-shadow: none !important;
    border: 1px solid #ccc !important;
    border-top: none !important;
    margin-top: -1px !important;
    font-family: inherit !important;
  }
  
  .pac-item {
    padding: 8px 10px !important;
    cursor: pointer !important;
  }
  
  .pac-item:hover {
    background-color: #f8f9fa !important;
  }
  
  .pac-icon {
    margin-right: 10px !important;
  }
  
  .pac-item-query {
    font-size: 14px !important;
  }
`;

interface SearchTabsProps {
  map: google.maps.Map | null;
  mapMode: 'pan' | 'marker' | 'polygon';
  setMapMode: (mode: 'pan' | 'marker' | 'polygon') => void;
  handleUseCurrentLocation: () => void;
  onPlaceChanged?: (location: { lat: number; lng: number }) => void;
  onPropertiesFound: (properties: any[]) => void;
  setIsSearching: (isSearching: boolean) => void;
  showPropertyInfoWindow?: (property: any, position: { lat: number; lng: number }, isPersonSearch?: boolean) => void;
  setPropertyMarkers: (markers: any[]) => void;
  setLegendItems: (items: { type: string; color: string }[]) => void;
  getPropertyTypeColor: (type: string) => string;
  isMaximized?: boolean;
  toggleMaximized?: () => void;
}

const SearchTabs: React.FC<SearchTabsProps> = ({
  map,
  mapMode,
  setMapMode,
  handleUseCurrentLocation,
  onPlaceChanged,
  onPropertiesFound,
  setIsSearching,
  showPropertyInfoWindow,
  setPropertyMarkers,
  setLegendItems,
  getPropertyTypeColor,
  isMaximized = false,
  toggleMaximized
}) => {
  const [activeTab, setActiveTab] = useState<'place' | 'person'>('place');
  const [searchText, setSearchText] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [foundProperties, setFoundProperties] = useState<any[]>([]);
  const [showPropertyDropdown, setShowPropertyDropdown] = useState(false);
  const [selectedPropertyIndex, setSelectedPropertyIndex] = useState<number | null>(null);
  
  const searchBoxRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  // Inject custom styles for the Google Places Autocomplete dropdown
  useEffect(() => {
    // Create a style element
    const styleElement = document.createElement('style');
    styleElement.type = 'text/css';
    styleElement.id = 'google-places-autocomplete-styles';
    styleElement.appendChild(document.createTextNode(autocompleteStyles));
    
    // Add the style element to the document head
    document.head.appendChild(styleElement);
    
    // Clean up function to remove the style element when the component unmounts
    return () => {
      const existingStyle = document.getElementById('google-places-autocomplete-styles');
      if (existingStyle) {
        document.head.removeChild(existingStyle);
      }
    };
  }, []);

  // Initialize autocomplete when map is loaded
  useEffect(() => {
    if (map && searchBoxRef.current && (!autocompleteRef.current || activeTab === 'place')) {
      // Clean up previous autocomplete instance if it exists
      if (autocompleteRef.current) {
        // Remove previous listeners to avoid memory leaks
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
      
      // Create new autocomplete instance
      const autocomplete = new google.maps.places.Autocomplete(searchBoxRef.current, {
        componentRestrictions: { country: 'za' },
        fields: ['geometry', 'name', 'formatted_address']
      });
      
      // Store the autocomplete instance
      autocompleteRef.current = autocomplete;
      
      // Add place_changed event listener
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        
        if (place.geometry && place.geometry.location) {
          const location = {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng()
          };
          
          // Call the onPlaceChanged callback if provided
          if (onPlaceChanged) {
            onPlaceChanged(location);
          }
          
          // Update search text with formatted address if available
          if (place.formatted_address) {
            setSearchText(place.formatted_address);
          } else if (place.name) {
            setSearchText(place.name);
          }
          
          // Change to pan mode after place is selected
          setMapMode('pan');
        }
      });

      // Apply custom styles to the autocomplete dropdown
      const pacContainer = document.querySelector('.pac-container');
      if (pacContainer) {
        // Remove the shadow
        (pacContainer as HTMLElement).style.boxShadow = 'none';
        (pacContainer as HTMLElement).style.border = '1px solid #ccc';
      }
    }
  }, [map, activeTab, onPlaceChanged, setMapMode]);

  // Handle input change to track search text
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchText(value);
    
    // If the input is cleared, reinitialize the autocomplete
    if (value === '' && map && searchBoxRef.current) {
      // Clean up previous autocomplete instance if it exists
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
      
      // Create new autocomplete instance
      const autocomplete = new google.maps.places.Autocomplete(searchBoxRef.current, {
        componentRestrictions: { country: 'za' },
        fields: ['geometry', 'name', 'formatted_address']
      });
      
      // Store the autocomplete instance
      autocompleteRef.current = autocomplete;
      
      // Add place_changed event listener
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        
        if (place.geometry && place.geometry.location) {
          const location = {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng()
          };
          
          // Call the onPlaceChanged callback if provided
          if (onPlaceChanged) {
            onPlaceChanged(location);
          }
          
          // Update search text with formatted address if available
          if (place.formatted_address) {
            setSearchText(place.formatted_address);
          } else if (place.name) {
            setSearchText(place.name);
          }
          
          // Change to pan mode after place is selected
          setMapMode('pan');
        }
      });
    }
  };

  // Handle ID number input change
  const handleIdNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow digits and limit to 13 characters
    const value = e.target.value.replace(/\D/g, '').slice(0, 13);
    setIdNumber(value);
  };

  // Handle key press for ID input
  const handleIdKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && idNumber.length === 13) {
      handleSearchById();
    }
  };

  // Handle clear button click for place search
  const handleClearSearch = () => {
    setSearchText('');
    if (searchBoxRef.current) {
      searchBoxRef.current.value = '';
      searchBoxRef.current.focus(); // Focus the input after clearing
    }
    
    // Reinitialize autocomplete after clearing
    if (map && searchBoxRef.current) {
      // Clean up previous autocomplete instance if it exists
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
      
      // Create new autocomplete instance
      const autocomplete = new google.maps.places.Autocomplete(searchBoxRef.current, {
        componentRestrictions: { country: 'za' },
        fields: ['geometry', 'name', 'formatted_address']
      });
      
      // Store the autocomplete instance
      autocompleteRef.current = autocomplete;
      
      // Add place_changed event listener
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        
        if (place.geometry && place.geometry.location) {
          const location = {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng()
          };
          
          // Call the onPlaceChanged callback if provided
          if (onPlaceChanged) {
            onPlaceChanged(location);
          }
        }
      });
    }
  };

  // Handle clear button click for ID search
  const handleClearIdSearch = () => {
    setIdNumber('');
  };

  // Handle clear button click for property dropdown
  const handleClearPropertyDropdown = () => {
    setShowPropertyDropdown(false);
    setFoundProperties([]);
    setSelectedPropertyIndex(null);
    setIdNumber('');
  };

  // Format property for display in dropdown
  const formatPropertyForDisplay = (property: any) => {
    // Get property name or type, convert to title case
    const propertyName = property.propertyName || property.propertyType || 'Property';
    const formattedPropertyName = propertyName
      .split(' ')
      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
    
    // Format address components
    const addressParts = [
      property.streetNumber, 
      property.streetName ? property.streetName.split(' ')
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ') : '',
      property.streetType ? property.streetType.charAt(0).toUpperCase() + property.streetType.slice(1).toLowerCase() : ''
    ].filter(Boolean);
    
    // Join address parts with spaces
    const address = addressParts.join(' ');
    
    // If street name is empty, use suburb as address
    let displayAddress = address;
    if (!address && property.suburb) {
      displayAddress = property.suburb.split(' ')
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    } else if (!address) {
      displayAddress = 'Address not available';
    }
    
    return `${formattedPropertyName}: ${displayAddress}`;
  };

  // Handle property selection from dropdown
  const handlePropertySelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const index = parseInt(e.target.value, 10);
    setSelectedPropertyIndex(index);
    
    if (index >= 0 && index < foundProperties.length) {
      const selectedProperty = foundProperties[index];
      
      if (selectedProperty.latitude && selectedProperty.longitude && map) {
        const lat = parseFloat(selectedProperty.latitude);
        const lng = parseFloat(selectedProperty.longitude);
        
        if (!isNaN(lat) && !isNaN(lng)) {
          // Pan to the property location
          map.panTo({ lat, lng });
          map.setZoom(18);
          
          // Create a property object with lat/lng for the info window
          const propertyWithPosition = {
            ...selectedProperty,
            lat,
            lng
          };
          
          // Show the property info window
          if (showPropertyInfoWindow) {
            showPropertyInfoWindow(propertyWithPosition, { lat, lng }, true);
          }
        }
      }
    }
  };

  // Handle search by ID number
  const handleSearchById = async () => {
    if (idNumber.length !== 13) {
      toast.error("Please enter a valid 13-digit SA ID number");
      return;
    }

    setIsSearching(true);
    
    // Clear existing markers and properties before starting a new search
    setPropertyMarkers([]);
    setLegendItems([]);
    
    try {
      const response = await apiAgent.PropertyReports.searchPropertiesByOwner(idNumber);
      
      if (response && Array.isArray(response)) {
        // Process the properties - ensure this is called even if response is empty
        
        // Always call onPropertiesFound before processing markers and legend items
        onPropertiesFound(response);
        
        // Create markers for the properties
        if (response.length > 0) {
          const markers = createPropertyMarkers(response);
          setPropertyMarkers(markers);
          
          // Create legend items
          const items = createLegendItems(response, getPropertyTypeColor);
          setLegendItems(items);
          
          // Store the found properties and show the dropdown
          setFoundProperties(response);
          setShowPropertyDropdown(true);
          setSelectedPropertyIndex(0); // Select the first property by default
          
          // If properties were found, center the map on the first property
          if (map) {
            const firstProperty = response[0];
            if (firstProperty.latitude && firstProperty.longitude) {
              const lat = parseFloat(firstProperty.latitude);
              const lng = parseFloat(firstProperty.longitude);
              
              if (!isNaN(lat) && !isNaN(lng)) {
                map.panTo({ lat, lng });
                map.setZoom(12); // Zoom out a bit to show multiple properties
                
                // Create a property object with lat/lng for the info window
                const propertyWithPosition = {
                  ...firstProperty,
                  lat,
                  lng
                };
                
                // Trigger opening of the first property's info window
                // We need to add a small delay to ensure the map has finished panning
                if (showPropertyInfoWindow) {
                  setTimeout(() => {
                    if (map && firstProperty) {
                      showPropertyInfoWindow(propertyWithPosition, { lat, lng }, true);
                    }
                  }, 300);
                }
              }
            }
          }
        } else {
          // Show toast notification when no properties are found
          toast.info(`No properties found for ID number ${idNumber}`);
          
          // Clear the dropdown
          setFoundProperties([]);
          setShowPropertyDropdown(false);
          setSelectedPropertyIndex(null);
        }
        
        // Change to pan mode after search is completed
        setMapMode('pan');
      } else {
        toast.error("Invalid response from server");
      }
    } catch (error) {
      console.error("Error searching properties by ID:", error);
      toast.error("Error searching properties by ID");
    } finally {
      setIsSearching(false);
    }
  };

  // Handle tab switch
  const handleTabSwitch = (tab: 'place' | 'person') => {
    setActiveTab(tab);
    setSearchText('');
    setIdNumber('');
    setFoundProperties([]);
    setShowPropertyDropdown(false);
    setSelectedPropertyIndex(null);
    
    // If switching to place tab, reset the search box
    if (tab === 'place' && searchBoxRef.current) {
      searchBoxRef.current.value = '';
      
      // Reinitialize autocomplete
      if (map) {
        // Clean up previous autocomplete instance if it exists
        if (autocompleteRef.current) {
          google.maps.event.clearInstanceListeners(autocompleteRef.current);
          autocompleteRef.current = null;
        }
        
        // Create new autocomplete instance
        const autocomplete = new google.maps.places.Autocomplete(searchBoxRef.current, {
          componentRestrictions: { country: 'za' },
          fields: ['geometry', 'name', 'formatted_address']
        });
        
        // Store the autocomplete instance
        autocompleteRef.current = autocomplete;
        
        // Add place_changed event listener
        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          
          if (place.geometry && place.geometry.location) {
            const location = {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng()
            };
            
            // Call the onPlaceChanged callback if provided
            if (onPlaceChanged) {
              onPlaceChanged(location);
            }
            
            // Update search text with formatted address if available
            if (place.formatted_address) {
              setSearchText(place.formatted_address);
            } else if (place.name) {
              setSearchText(place.name);
            }
            
            // Change to pan mode after place is selected
            setMapMode('pan');
          }
        });
      }
    }
  };

  return (
    <div className={`map-search-tabs ${isMaximized ? 'map-search-tabs-maximized' : ''}`}>
      <div className="search-tabs-container">
        <div className="search-tabs-header">
          <div className="search-tabs-buttons">
            <button
              className={`search-tab-btn ${activeTab === 'place' ? 'active' : ''}`}
              onClick={() => handleTabSwitch('place')}
              title="Search by location"
            >
              <FontAwesomeIcon icon={faMapMarkerAlt} />
              <span>Place</span>
            </button>
            <button
              className={`search-tab-btn ${activeTab === 'person' ? 'active' : ''}`}
              onClick={() => handleTabSwitch('person')}
              title="Search by person"
            >
              <FontAwesomeIcon icon={faUser} />
              <span>Person</span>
            </button>
          </div>
        </div>
        
        {/* Tab content */}
        <div className="search-tabs-content">
          {/* Place search tab */}
          {activeTab === 'place' && (
            <div className="search-tab-panel">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%' }}>
                <div className="search-input-group" style={{ 
                  flex: '1', 
                  marginBottom: 0,
                  position: 'relative', 
                  zIndex: isMaximized ? 10001 : 2 // Higher z-index when maximized
                }}>
                  <input
                    ref={searchBoxRef}
                    type="text"
                    placeholder="Search for a place"
                    className="search-input"
                    value={searchText}
                    onChange={handleSearchInputChange}
                  />
                  {searchText && (
                    <button
                      className="search-clear-btn"
                      onClick={handleClearSearch}
                      title="Clear search"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  )}
                  <button
                    className="search-location-btn"
                    onClick={handleUseCurrentLocation}
                    title="Use current location"
                  >
                    <FontAwesomeIcon icon={faLocationCrosshairs} />
                  </button>
                </div>
                <div className="search-map-tools" style={{ flex: '0 0 auto', margin: 0 }}>
                  <button
                    className={`search-map-tool-btn ${mapMode === 'pan' ? 'active' : ''}`}
                    onClick={() => setMapMode('pan')}
                    title="Pan tool"
                  >
                    <FontAwesomeIcon icon={faHand} />
                  </button>
                  <button
                    className={`search-map-tool-btn ${mapMode === 'marker' ? 'active' : ''}`}
                    onClick={() => setMapMode('marker')}
                    title="Place marker tool"
                  >
                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                  </button>
                  <button
                    className={`search-map-tool-btn ${mapMode === 'polygon' ? 'active' : ''}`}
                    onClick={() => {
                      if (mapMode === 'polygon') {
                        // If already in polygon mode, dispatch a custom event to clear existing polygon
                        const event = new CustomEvent('clearPolygon');
                        document.dispatchEvent(event);
                      } else {
                        setMapMode('polygon');
                      }
                    }}
                    title="Draw search area tool"
                  >
                    <FontAwesomeIcon icon={faPencilAlt} />
                  </button>
                  {/* Add maximize button */}
                  {toggleMaximized && (
                    <button
                      className="search-map-tool-btn"
                      onClick={toggleMaximized}
                      title={isMaximized ? "Exit fullscreen" : "Enter fullscreen"}
                    >
                      <FontAwesomeIcon icon={isMaximized ? faCompress : faExpand} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Person search tab */}
          {activeTab === 'person' && (
            <div className="search-tab-panel">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%' }}>
                <div className="search-input-group" style={{ 
                  flex: '1', 
                  marginBottom: 0,
                  position: 'relative', 
                  zIndex: isMaximized ? 10001 : 2 // Higher z-index when maximized
                }}>
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Enter 13-digit ID number"
                    value={idNumber}
                    onChange={handleIdNumberChange}
                    onKeyPress={handleIdKeyPress}
                  />
                  {idNumber && (
                    <button
                      className="search-clear-btn"
                      onClick={handleClearIdSearch}
                      title="Clear search"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  )}
                  {idNumber.length === 13 && (
                    <button
                      className="search-btn"
                      onClick={handleSearchById}
                      title="Search by ID"
                    >
                      <FontAwesomeIcon icon={faSearch} />
                    </button>
                  )}
                </div>
                <div className="search-map-tools" style={{ flex: '0 0 auto', margin: 0 }}>
                  <button
                    className={`search-map-tool-btn ${mapMode === 'pan' ? 'active' : ''}`}
                    onClick={() => setMapMode('pan')}
                    title="Pan tool"
                  >
                    <FontAwesomeIcon icon={faHand} />
                  </button>
                  {/* Add maximize button */}
                  {toggleMaximized && (
                    <button
                      className="search-map-tool-btn"
                      onClick={toggleMaximized}
                      title={isMaximized ? "Exit fullscreen" : "Enter fullscreen"}
                    >
                      <FontAwesomeIcon icon={isMaximized ? faCompress : faExpand} />
                    </button>
                  )}
                </div>
              </div>
              
              {/* Property dropdown list */}
              {foundProperties.length > 0 && (
                <div className="search-property-dropdown" style={{ marginTop: '10px' }}>
                  <select
                    value={selectedPropertyIndex !== null ? selectedPropertyIndex.toString() : ''}
                    onChange={handlePropertySelect}
                    className="search-property-select"
                  >
                    <option value="" disabled>Select a property</option>
                    {foundProperties.map((property, index) => (
                      <option key={property.propertyId} value={index.toString()}>
                        {formatPropertyForDisplay(property)}
                      </option>
                    ))}
                  </select>
                  {selectedPropertyIndex !== null && (
                    <button 
                      className="search-clear-btn" 
                      onClick={handleClearPropertyDropdown}
                      title="Clear selection"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchTabs; 